// Lightweight client-side SEO helper (title, meta, Open Graph, canonical, JSON-LD).

// Single source of truth for the public site base URL. Set REACT_APP_PUBLIC_SITE_URL
// (e.g. https://uniincorp.com) in production; falls back to the current host in preview.
export const SITE_URL = (
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_PUBLIC_SITE_URL) ||
  (typeof window !== "undefined" ? window.location.origin : "")
).replace(/\/+$/, "");

export const DEFAULT_OG_IMAGE = "https://images.unsplash.com/photo-1531819177115-428566ccfb50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const SITE_HOST = (() => { try { return new URL(SITE_URL).host; } catch { return ""; } })();
const PRIVATE_PREFIXES = ["/admin", "/dashboard", "/login", "/start", "/requests", "/payment", "/u", "/api"];

// Segment-aware match: "/payment" blocks "/payment/success" but NOT "/payment-gateway".
function isPrivatePath(pathname) {
  return PRIVATE_PREFIXES.some((p) => pathname === p || pathname === p + "/" || pathname.startsWith(p + "/"));
}

// Return a safe, absolute canonical on the production host. Never point canonical at
// staging hosts or private/authenticated routes.
function safeCanonical(url) {
  if (!url) return "";
  try {
    const u = new URL(url, SITE_URL || undefined);
    if (SITE_HOST && u.host !== SITE_HOST) return `${SITE_URL}${u.pathname}${u.search}`;
    if (PRIVATE_PREFIXES.some((p) => isPrivatePath(u.pathname))) return SITE_URL || u.origin;
    return `${u.origin}${u.pathname}${u.search}`;
  } catch {
    return SITE_URL || "";
  }
}

// Centralized, reusable schema.org builders (business identity from a single source).
export function orgJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "UNICORP Services Limited",
    alternateName: "UNICORP",
    url: SITE_URL || undefined,
    logo: SITE_URL ? `${SITE_URL}/favicon.ico` : undefined,
    description: "Independent private corporate services provider assisting with company formation, banking introduction assistance, accounting, tax and compliance across leading jurisdictions.",
    areaServed: "Worldwide",
  };
}

export function websiteJsonLd() {
  return { "@context": "https://schema.org", "@type": "WebSite", name: "UNICORP", url: SITE_URL || undefined };
}

function upsertMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr[0], attr[1]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export function setSeo({ title, description, image, url, noindex, type }) {
  const img = image || DEFAULT_OG_IMAGE;
  const canon = safeCanonical(url);
  if (title) document.title = title;
  upsertMeta('meta[name="description"]', ["name", "description"], description);
  // robots is owned per-route by LangRoute.setAlternates; setSeo only overrides it when a
  // page explicitly passes noindex (e.g. 404/private screens) — never clobber otherwise.
  if (noindex !== undefined) upsertMeta('meta[name="robots"]', ["name", "robots"], noindex ? "noindex, nofollow" : "index, follow");
  upsertMeta('meta[property="og:site_name"]', ["property", "og:site_name"], "UNICORP");
  upsertMeta('meta[property="og:title"]', ["property", "og:title"], title);
  upsertMeta('meta[property="og:description"]', ["property", "og:description"], description);
  upsertMeta('meta[property="og:image"]', ["property", "og:image"], img);
  upsertMeta('meta[property="og:type"]', ["property", "og:type"], type || "website");
  upsertMeta('meta[property="og:url"]', ["property", "og:url"], canon);
  upsertMeta('meta[name="twitter:card"]', ["name", "twitter:card"], "summary_large_image");
  upsertMeta('meta[name="twitter:title"]', ["name", "twitter:title"], title);
  upsertMeta('meta[name="twitter:description"]', ["name", "twitter:description"], description);
  upsertMeta('meta[name="twitter:image"]', ["name", "twitter:image"], img);
  if (canon) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canon);
  }
}

export function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function clearJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

// Multilingual: emit reciprocal hreflang alternates + x-default for a path.
// Only published languages (approved equivalents) are included; English is always x-default.
const LANG_PREFIX = { en: "", vi: "vi", "zh-Hans": "zh-hans", fr: "fr", es: "es" };
function stripLangPrefix(pathname) {
  const seg = pathname.replace(/^\/+/, "").split("/")[0].toLowerCase();
  if (["vi", "zh-hans", "fr", "es"].includes(seg)) {
    const rest = pathname.replace(new RegExp(`^/${seg}`, "i"), "") || "/";
    return rest.startsWith("/") ? rest : "/" + rest;
  }
  return pathname || "/";
}
export function setAlternates(pathname, publishedCodes = [], opts = {}) {
  document.head.querySelectorAll('link[rel="alternate"][data-i18n]').forEach((e) => e.remove());
  if (opts.skip) return; // private routes manage robots via their own setSeo({ noindex: true })
  // robots is decided here per route: noindex for incomplete localized pages
  // (duplicate/English-fallback content), index otherwise.
  upsertMeta('meta[name="robots"]', ["name", "robots"], opts.noindex ? "noindex, nofollow" : "index, follow");
  if (!SITE_URL) return;
  const base = stripLangPrefix(pathname);
  const href = (code) => `${SITE_URL}${LANG_PREFIX[code] ? "/" + LANG_PREFIX[code] : ""}${base === "/" ? "" : base}` || SITE_URL;
  const add = (hreflang, url) => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate");
    l.setAttribute("hreflang", hreflang);
    l.setAttribute("href", url);
    l.setAttribute("data-i18n", "1");
    document.head.appendChild(l);
  };
  // Only emit alternates when there is at least one published non-English equivalent —
  // a lone self-referencing hreflang adds no value and risks referencing incomplete pages.
  if (!publishedCodes || publishedCodes.length === 0) return;
  add("en", href("en"));
  add("x-default", href("en"));
  publishedCodes.forEach((code) => { if (LANG_PREFIX[code]) add(code.toLowerCase(), href(code)); });
}

export function resetSeo() {
  setSeo({
    title: "UNICORP — Expand Beyond Borders",
    description: "Premium global business incorporation, banking and compliance across leading jurisdictions. Transparent, all-inclusive pricing with no hidden fees.",
    url: window.location.origin,
  });
}
